import { useEffect, useRef, useState, type ReactNode } from "react";
import { z } from "zod";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { keys, useLocations, useMe } from "../api/hooks";
import { derivePulse } from "../lib/pulse";
import { startUsageTracking } from "../lib/usage";
import { setUsageTracker, trackAction, trackPageView } from "../lib/usage-track";
import { Button, cx, useModalFocus } from "./ui";
import { StatusDot } from "./StatusDot";
import { Timestamp } from "./Timestamp";

/** A pill tab (Figma): blush fill and crimson text when active, ink otherwise. */
export const navLink = ({ isActive }: { isActive: boolean }) => cx(
  "flex h-[35px] w-full items-center justify-center rounded-full px-3.5 text-sm font-semibold transition-colors",
  isActive ? "bg-hs-primary-soft text-hs-primary" : "text-hs-text hover:bg-hs-primary-soft/50",
);

/** Hello Central's support center, opened straight onto the "Submit New Ticket" form with the BTB Manager category picked. */
export const SUPPORT_URL = "https://central.hellosugar.salon/support-center?new=1&category=btb_manager";

/** The Support tab: an external link styled like the other pill tabs (never active), opening Hello Central in a new tab. */
export function SupportLink() {
  return (
    <a href={SUPPORT_URL} onClick={() => trackAction("support_clicked")} target="_blank" rel="noopener noreferrer" className={cx(navLink({ isActive: false }), "gap-1.5")}>
      Support
      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-hs-text-muted">
        <path d="M5 2H2.5A.5.5 0 0 0 2 2.5v7a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5V7M7 2h3v3M10 2 5.5 6.5" />
      </svg>
    </a>
  );
}

/**
 * The Figma rail (logo, "Block-til-Busy Manager", pill tabs) beside a white workspace; a top bar and drawer below `lg`.
 * The mode / pulse block and the account row the Figma leaves out sit under the tabs, smaller, because owners
 * and the tests rely on them. Owner and admin layouts differ only in `nav` and the Admin tag. The pulse reads
 * the locations query the pages already use, so it costs no extra request; if that query fails the rail shows
 * dashes, not an error.
 */
export function Shell({ nav, admin }: { nav: ReactNode; admin?: boolean }) {
  const me = useMe().data!;
  const locations = useLocations();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [pathname]);
  // Owner sessions only (the server folds the role into usageTracking). Stop-then-start keeps StrictMode's double run to one live tracker.
  const tracking = me.usageTracking;
  const csrf = me.csrfToken;
  useEffect(() => {
    if (!tracking) return;
    const tracker = startUsageTracking({ csrf });
    setUsageTracker(tracker);
    return () => { tracker.stop(); setUsageTracker(null); };
  }, [tracking, csrf]);
  useEffect(() => { trackPageView(pathname); }, [pathname, tracking, csrf]);
  const signOut = async () => { await api("/auth/logout", z.undefined(), { method: "POST" }); qc.setQueryData(keys.me, null); navigate("/login"); };
  const live = me.mode === "live";
  const pulse = derivePulse(locations.data, me);

  const rail = (
    <div className="flex h-full w-[182px] flex-col overflow-y-auto border-r border-hs-divider bg-hs-rail px-6 pb-4 pt-3.5">
      <img src="/ui/logo-black.png" alt="Hello Sugar" className="h-[82px] w-[125px] object-contain object-left" />
      <div className="mt-[26px] flex items-start gap-1">
        <img src="/ui/calendar.svg" alt="" width={24} height={24} className="h-6 w-6 shrink-0" />
        <span className="text-sm font-semibold leading-6 text-hs-primary">Block-til-Busy Manager</span>
        {admin && <span className="ml-auto rounded-full border border-hs-border-strong px-2 text-[11px] font-medium leading-5 text-hs-text-muted">Admin</span>}
      </div>
      <nav aria-label="Main" className={cx("mt-[26px] flex flex-col", admin ? "gap-1.5" : "gap-4")}>{nav}</nav>
      <div className="mt-6 border-t border-hs-divider pt-4">
        <StatusDot tone={live ? "success" : "neutral"} label={live ? "Live" : "Preview"} />
        <p className="mt-2 text-xs font-medium text-hs-text-muted">{live ? "Writing to Boulevard calendars." : "Planning only. Nothing is written to Boulevard."}</p>
        <dl className="mt-3 space-y-1.5 text-xs">
          <PulseRow label="Last plan" at={pulse.lastPlannedAt} />
          <PulseRow label="Last Boulevard event" at={pulse.lastEventAt} />
          <PulseRow label="Next nightly run" at={pulse.nextNightlyRunAt} />
        </dl>
      </div>
      <div className="mt-auto border-t border-hs-divider pt-3 text-xs text-hs-text-muted">
        <p className="truncate" title={me.email}>{me.email}</p>
        <Button variant="ghost" size="sm" className="mt-2 h-8 px-3 text-xs" onClick={signOut}>Sign out</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:sticky lg:top-0 lg:block lg:h-screen lg:shrink-0">{rail}</aside>
      <header className="flex h-14 items-center gap-3 border-b border-hs-divider bg-hs-rail px-4 lg:hidden">
        <button type="button" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)} className="rounded-md p-1.5 text-hs-text hover:bg-hs-surface">
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M3 5h14M3 10h14M3 15h14" /></svg>
        </button>
        <img src="/ui/calendar.svg" alt="" width={24} height={24} className="h-6 w-6" />
        <span className="text-sm font-semibold text-hs-primary">Block-til-Busy Manager</span>
        <span className="ml-auto"><StatusDot tone={live ? "success" : "neutral"} label={live ? "Live" : "Preview"} /></span>
      </header>
      {open && <Drawer onClose={() => setOpen(false)}>{rail}</Drawer>}
      <div className="min-w-0 flex-1">
        {me.globalPaused && (
          <div role="status" className="border-b border-hs-warning/40 bg-hs-warning-soft px-4 py-2 text-[13px] font-medium text-hs-warning lg:px-10">
            Paused by corporate. No calendar changes are being made.
          </div>
        )}
        <main className="mx-auto max-w-[1280px] px-4 py-6 lg:px-10 lg:pb-10 lg:pt-8"><Outlet /></main>
      </div>
    </div>
  );
}

/** The rail as a modal sheet below `lg`: takes focus, keeps Tab inside, locks page scroll, closes on Escape or backdrop. */
function Drawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  const onKeyDown = useModalFocus(panel, onClose);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);
  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={onKeyDown} className="absolute inset-y-0 left-0 shadow-lg outline-none">{children}</div>
    </div>
  );
}

function PulseRow({ label, at }: { label: string; at: string | null }) {
  return (
    // Wraps so a long value ("Tomorrow 3:00 AM") drops to its own right-aligned line instead of breaking mid-phrase.
    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
      <dt className="whitespace-nowrap text-hs-text-muted">{label}</dt>
      <dd className="ml-auto whitespace-nowrap font-medium text-hs-text"><Timestamp at={at} /></dd>
    </div>
  );
}
